import { useState } from 'react';
import {
    Alert,
    Box,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    ActionType,
    ConsentResponse,
    GetConsentsResponse
} from '@/models/company';
import { useFormatting } from '@/utils/formatting';
import {
    TableCell,
    TableHeader,
    TableRow,
    TableHeaderCell
} from '@/components/shared/styles/Table';
import { TableCard } from '@/components/shared/Card';
import CardHeader from '@/components/shared/CardHeader';
import { theme } from '@/lib/theme';
import { XSMedium } from '@/typography/Typography';
import { TransactionStatus } from '@/models/transaction';
import Chip from '@/components/shared/Chip';
import { PrimaryButton } from '@/components/shared/Button';
import { UploadLegalDocumentDialog } from './UploadLegalDocumentDialog';
import { ButtonContainer } from '@/pages/company/PaymentSetting';
import { useGetConsents } from '@/services/usersQuery';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';

const getStatusColor = (status: ActionType) => {
    switch (status) {
        case 'Accepted':
            return 'success';
        case 'Declined':
            return 'error';
        default:
            return 'warning';
    }
};

export const Status = ({ status }: any) => {
    return <Chip color={getStatusColor(status)} label={status} />;
};

export const ConsentsTable = () => {
    const { t } = useTranslation();
    const [openUploadDialog, setOpenUploadDialog] = useState(false);
    const { formatDateTime } = useFormatting();
    const { data, error, isLoading } = useGetConsents();

    useHandleResponse({ error });

    const consents = data?.data as GetConsentsResponse | undefined;

    const getTermsDocumentName = (consent: ConsentResponse) => {
        if (consent.termsVersionName) {
            return consent.termsVersionName;
        }
        switch (consent.consentType) {
            case 'Commercial':
                return t('license_agreement');
            case 'Data':
                return t('data_processing_agreement');
            case 'NewsletterAndUpdates':
                return t('marketing_updates');
            default:
                return consent.consentType;
        }
    };

    return (
        <>
            {error && <Alert severity="error">{error.message}</Alert>}
            {consents && consents.consents.length === 0 && (
                <Alert severity="info">{t('no_accepted_documents')}</Alert>
            )}
            {consents && consents.consents.length > 0 && (
                <TableCard>
                    <CardHeader
                        label={
                            <Stack direction={'row'} alignItems={'center'}>
                                {t('legal_documents')}
                            </Stack>
                        }
                    ></CardHeader>
                    <TableContainer>
                        <Table sx={{ minWidth: 650 }} aria-label="simple table">
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell
                                        sx={{
                                            fontWeight: 'bold',
                                            width: '45%'
                                        }}
                                    >
                                        {t('document_column')}
                                    </TableHeaderCell>
                                    <TableHeaderCell
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        {t('action_column')}
                                    </TableHeaderCell>
                                    <TableHeaderCell
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        {t('changed_at_column')}
                                    </TableHeaderCell>
                                    <TableHeaderCell
                                        align="right"
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        {t('accepted_signed_by_column')}
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {consents.consents.map((consent) => {
                                    return (
                                        <TableRow key={consent.consentId}>
                                            <TableCell>
                                                {consent.termsVersionUrl ? (
                                                    <a
                                                        href={
                                                            consent.termsVersionUrl
                                                        }
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        {getTermsDocumentName(
                                                            consent
                                                        )}
                                                    </a>
                                                ) : (
                                                    <>
                                                        {getTermsDocumentName(
                                                            consent
                                                        )}
                                                    </>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Status
                                                    status={consent.actionType}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {formatDateTime(
                                                    consent.performedAt
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {consent.performedByEmail ??
                                                    consent.performedByName}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <ButtonContainer style={{ borderTop: 'none' }}>
                        <PrimaryButton
                            onClick={() => setOpenUploadDialog(true)}
                            sx={{ marginLeft: '6px !important' }}
                            size="medium"
                        >
                            {t('upload_company_document_button')}
                        </PrimaryButton>
                    </ButtonContainer>
                </TableCard>
            )}
            <UploadLegalDocumentDialog
                open={openUploadDialog}
                onClose={() => setOpenUploadDialog(false)}
            />
        </>
    );
};
